const PROMO_STORAGE_KEY = 'fide_pricing_promo_code';
const DEFAULT_PROMO_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const getPromoStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
};

export const normalizePromotionCode = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().toUpperCase();
};

export const parsePromoExpirySeconds = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const normalizedValue = value.trim();
  if (!/^\d+$/.test(normalizedValue)) {
    return null;
  }

  const parsedValue = Number(normalizedValue);
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return null;
  }

  return parsedValue;
};

export const readStoredPromotionCode = () => {
  const storage = getPromoStorage();
  if (!storage) {
    return null;
  }

  try {
    const rawValue = storage.getItem(PROMO_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    const code = normalizePromotionCode(parsedValue?.code);
    const expiresAt = Number(parsedValue?.expiresAt);

    if (!code || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      storage.removeItem(PROMO_STORAGE_KEY);
      return null;
    }

    return { code, expiresAt };
  } catch {
    storage.removeItem(PROMO_STORAGE_KEY);
    return null;
  }
};

export const writeStoredPromotionCode = (value) => {
  const storage = getPromoStorage();
  if (!storage) {
    return;
  }

  storage.setItem(PROMO_STORAGE_KEY, JSON.stringify(value));
};

export const clearStoredPromotionCode = () => {
  const storage = getPromoStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(PROMO_STORAGE_KEY);
};

export const capturePromotionCodeFromLocation = ({ pathname, search, hash = '', allowedPaths = [] }) => {
  if (!allowedPaths.includes(pathname)) {
    return null;
  }

  const searchParams = new URLSearchParams(search);
  const promotionCode = normalizePromotionCode(searchParams.get('promocode'));
  const rawPromoExpiry = searchParams.get('promoexpiry');

  if (!promotionCode && rawPromoExpiry === null) {
    return null;
  }

  if (promotionCode) {
    const parsedPromoExpirySeconds = parsePromoExpirySeconds(rawPromoExpiry);
    const expiresAt =
      parsedPromoExpirySeconds === null ? Date.now() + DEFAULT_PROMO_TTL_MS : parsedPromoExpirySeconds * 1000;

    if (expiresAt > Date.now()) {
      writeStoredPromotionCode({ code: promotionCode, expiresAt });
    } else {
      clearStoredPromotionCode();
    }
  }

  searchParams.delete('promocode');
  searchParams.delete('promoexpiry');
  const nextSearch = searchParams.toString();
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash || ''}`;
};
